import type { Address, Hex } from "viem";
import type { PrivateKeyAccount } from "viem/accounts";
import { ViemChain, ZERO_HASH, type ChainWriter, type DirectoryEntry, type TokenInfo } from "@woodpecker/chain";
import { parseServiceName, pricingHash, type PriceDocument, type ServiceKind } from "@woodpecker/protocol";
import type { ConsoleConfig } from "../lib/config";
import { priceSummaryOf, type PriceSummary } from "../lib/format";

export function chainOf(config: ConsoleConfig, account?: PrivateKeyAccount): ChainWriter {
  return new ViemChain({
    rpcUrl: config.rpcUrl,
    addresses: {
      chainId: config.chainId,
      contractA: config.contractA,
      wpCredit: config.wpCredit,
      directory: config.directory,
      ...(config.extensionDirectory ? { extensionDirectory: config.extensionDirectory } : {}),
    },
    account,
    fromBlock: BigInt(config.fromBlock),
    logChunk: config.logChunk ? BigInt(config.logChunk) : undefined,
  });
}

/**
 * Whether the hash of the service's price document is committed in its directory entry (issue #3 item 11): the
 * fetched /pricing hashes to the committed value, the document changed since, or nothing is committed (zero).
 */
export type PriceCommitment = "committed" | "changed" | "none";

/** A directory entry as the Console shows it: the chain's record joined with the service's /pricing. */
export interface ServiceEntry {
  name: string; // steven@68df/gpt
  handle: string;
  serviceName: string;
  owner: Address;
  token: string; // the symbol
  tokenAddress: Address;
  submitter: Address; // token.service()
  rate: bigint; // wpCredit base units per token
  origin: string;
  kinds: ServiceKind[];
  labels: string[];
  icon?: string;
  price: PriceSummary;
  models?: string[];
  tool?: string;
  defaultCap?: number;
  batch: number | "turn";
  ready: boolean;
  reachable: boolean;
  active: boolean;
  mine: boolean;
  priceCommitment: PriceCommitment;
  /** The hash committed in the entry (ZERO_HASH = none). */
  pricingHash: Hex;
  doc?: PriceDocument;
  info: TokenInfo;
}

export interface TokenMeta {
  symbol: string;
  address: Address;
  rate: bigint; // 0 for wpCredit
  decimals: number;
  owner: Address;
  service: Address;
}

async function fetchPricing(origin: string, timeoutMs = 8000): Promise<PriceDocument | null> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(`${origin}/pricing`, { signal: ctl.signal });
    if (!res.ok) return null;
    return (await res.json()) as PriceDocument;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function entryOf(
  chain: ChainWriter,
  record: DirectoryEntry,
  wallet: Address | null,
): Promise<ServiceEntry> {
  const info = await chain.tokenInfo(record.token);
  const name = `${record.handleName}@${record.addr4}/${record.serviceName}`;
  const doc = record.active ? await fetchPricing(record.url) : null;
  const kinds = doc?.kinds ?? [];
  const committed = record.pricingHash.toLowerCase() as Hex;
  const priceCommitment: PriceCommitment =
    committed === ZERO_HASH ? "none" : doc && pricingHash(doc).toLowerCase() === committed ? "committed" : "changed";
  return {
    name,
    handle: `${record.handleName}@${record.addr4}`,
    serviceName: record.serviceName,
    owner: record.owner,
    token: doc?.token.symbol || info.symbol,
    tokenAddress: record.token,
    submitter: info.service,
    rate: info.rate,
    origin: record.url,
    kinds,
    labels: doc?.labels ?? [],
    icon: doc?.icon,
    price: doc ? priceSummaryOf(doc) : { mode: "fixed", amount: 1, max: 1 },
    models: doc?.models,
    tool: doc?.tool,
    defaultCap: doc?.defaultCap,
    batch: doc?.batch ?? 1,
    ready: doc?.ready ?? false,
    reachable: doc !== null,
    active: record.active,
    mine: !!wallet && record.owner.toLowerCase() === wallet.toLowerCase(),
    priceCommitment,
    pricingHash: committed,
    doc: doc ?? undefined,
    info,
  };
}

/** The whole directory (D42): every token's record, joined with its /pricing. */
export async function loadDirectory(chain: ChainWriter, wallet: Address | null): Promise<ServiceEntry[]> {
  const n = await chain.dirCount();
  const records: DirectoryEntry[] = [];
  for (let i = 0; i < n; i++) records.push(await chain.dirEntryAt(i));
  const entries = await Promise.all(records.filter((r) => r.active).map((r) => entryOf(chain, r, wallet)));
  return entries;
}

export function tokenMetaOf(entries: ServiceEntry[], wpCredit: Address): Map<string, TokenMeta> {
  const out = new Map<string, TokenMeta>();
  out.set("wpCredit", {
    symbol: "wpCredit",
    address: wpCredit,
    rate: 1_000_000n,
    decimals: 6,
    owner: wpCredit,
    service: wpCredit,
  });
  for (const e of entries) {
    if (!out.has(e.token))
      out.set(e.token, {
        symbol: e.token,
        address: e.tokenAddress,
        rate: e.rate,
        decimals: e.info.decimals,
        owner: e.info.owner,
        service: e.info.service,
      });
  }
  return out;
}

export function findEntry(entries: ServiceEntry[], name: string): ServiceEntry | undefined {
  return entries.find((e) => e.name === name);
}

export function parseName(name: string) {
  return parseServiceName(name);
}

export type { Address, Hex };
